import { useEffect, useMemo, useState } from "react";
import { parseReceiptMarkup } from "@/lib/receiptMarkup";
import { renderReceiptLogoPreview } from "@/lib/receiptLogoPreview";
import {
  buildReceiptPreviewModel,
  receiptPreviewFontFamilyCss,
  receiptPreviewFontSizePx,
  type ReceiptPreviewLine,
  type ReceiptSeparatorStyle,
  type ReceiptTextAlignment,
  type ReceiptFontFamily,
  type ReceiptFontSize,
} from "@/lib/receiptPreview";

interface ReceiptPreviewProps {
  headerText: string;
  footerText: string;
  logoData?: string;
  includeCardPayment: boolean;
  charactersPerLine: number;
  separatorStyle?: ReceiptSeparatorStyle;
  headerAlign?: ReceiptTextAlignment;
  footerAlign?: ReceiptTextAlignment;
  titleText?: string;
  fontSize?: ReceiptFontSize;
  fontFamily?: ReceiptFontFamily;
  logoWidthPercent?: number;
}

function ReceiptFormattedLine({ text }: { text: string }) {
  const segments = useMemo(() => parseReceiptMarkup(text), [text]);

  return (
    <span className="whitespace-pre">
      {segments.map((segment, index) => {
        if (!segment.text) {
          return null;
        }
        const className = [
          segment.style.bold ? "font-bold" : "",
          segment.style.italic ? "italic" : "",
          segment.style.underline ? "underline" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <span key={`${index}-${segment.text}`} className={className || undefined}>
            {segment.text}
          </span>
        );
      })}
    </span>
  );
}

function ReceiptPreviewLineView({ line }: { line: ReceiptPreviewLine }) {
  if (!line.text && line.kind !== "footer") {
    return <div className="h-2" />;
  }

  return (
    <div
      className={[
        "leading-relaxed",
        line.align === "center" ? "text-center" : "",
        line.align === "right" ? "text-right" : "",
        line.kind === "title" ? "font-semibold tracking-[0.2em]" : "",
        line.kind === "separator" ? "text-label-tertiary" : "",
        line.kind === "tse" ? "text-[0.95em]" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ReceiptFormattedLine text={line.text} />
    </div>
  );
}

export function ReceiptPreview({
  headerText,
  footerText,
  logoData,
  includeCardPayment,
  charactersPerLine,
  separatorStyle,
  headerAlign,
  footerAlign,
  titleText,
  fontSize,
  fontFamily,
  logoWidthPercent,
}: ReceiptPreviewProps) {
  const [processedLogo, setProcessedLogo] = useState<string | null>(null);

  const model = useMemo(
    () =>
      buildReceiptPreviewModel({
        headerText,
        footerText,
        logoConfigured: Boolean(logoData),
        includeCardPayment,
        charactersPerLine,
        separatorStyle,
        headerAlign,
        footerAlign,
        titleText,
        fontSize,
        fontFamily,
      }),
    [
      charactersPerLine,
      footerAlign,
      footerText,
      fontFamily,
      fontSize,
      headerAlign,
      headerText,
      includeCardPayment,
      logoData,
      separatorStyle,
      titleText,
    ],
  );

  useEffect(() => {
    if (!logoData) {
      setProcessedLogo(null);
      return;
    }

    let cancelled = false;
    void renderReceiptLogoPreview(logoData, charactersPerLine, logoWidthPercent)
      .then((preview) => {
        if (!cancelled) {
          setProcessedLogo(preview);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProcessedLogo(logoData);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [charactersPerLine, logoData, logoWidthPercent]);

  const bodyStartIndex = model.lines.findIndex((line) => line.kind === "title");

  return (
    <div className="rounded-xl border border-separator bg-grouped p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-label-secondary">Bon-Vorschau</span>
        <span className="text-xs text-label-tertiary">{model.width} Zeichen · Drucksimulation</span>
      </div>

      <div className="mt-3 flex justify-center">
        <div
          className="w-full max-w-[24rem] rounded-sm border border-black/10 bg-white px-4 py-5 text-black shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
          style={{
            fontFamily: receiptPreviewFontFamilyCss(model.fontFamily),
            fontSize: `${receiptPreviewFontSizePx(model.fontSize)}px`,
          }}
          aria-label="Kassenbon-Vorschau"
        >
          {model.lines.slice(0, bodyStartIndex >= 0 ? bodyStartIndex : model.lines.length).map((line, index) => (
            <ReceiptPreviewLineView key={`pre-${index}-${line.text}`} line={line} />
          ))}

          {processedLogo ? (
            <div className="my-3 flex justify-center">
              <img
                src={processedLogo}
                alt="Bon-Logo Druckvorschau"
                className="h-auto max-w-full object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
          ) : null}

          {model.lines.slice(bodyStartIndex >= 0 ? bodyStartIndex : model.lines.length).map((line, index) => (
            <ReceiptPreviewLineView key={`post-${index}-${line.text}`} line={line} />
          ))}
        </div>
      </div>

      {logoData ? (
        <p className="mt-2 text-xs text-label-tertiary">
          Logo-Vorschau entspricht der ESC/POS-Aufbereitung (Schwarzweiß, skaliert auf gewählte Logo-Breite).
        </p>
      ) : null}
    </div>
  );
}
